import { Fragment, useState } from "react";
import type {
  Citation,
  DocumentFindingsResponse,
  ElementFindingResponse,
  RungFindingResponse,
} from "@patent-studio/api-types";
import { cn } from "@/lib/utils";
import { MicroLabel } from "@/components/ui/MicroLabel";
import type { Tone } from "@/components/ui/tone";
import { toneClasses } from "@/components/ui/tone";
import { Panel } from "@/components/ui/Panel";
import { DisclosureHeader, useDisclosure } from "@/components/ui/Disclosure";
import { plural } from "@/lib/plural";
import { OpenDocument, pageReference } from "../../documents/OpenDocument";

/** How a document is named: its D-number, or the file's own name for one the task no longer holds. */
function referenceOf(document: DocumentFindingsResponse): string {
  return document.reference_id ?? document.source_file;
}

/** How far one document got on this position: the chip's two readings, and the rung-only third. */
export type DocumentReach = "discloses" | "partial" | "narrower";

/**
 * A document reaches a position when it discloses one of its elements, or, where
 * it discloses none of them, when it discloses a rung beneath one. A document
 * whose reading could not be verified reaches nothing, as the server's rules
 * read nothing from it.
 *
 * Nothing about a rung moves the verdict or the served chip, which read the
 * elements alone. A document that reaches a rung and not the element above it is
 * where the margin sits, so the page has to offer it rather than drop it for
 * having no chip.
 */
export function reachOf(
  document: DocumentFindingsResponse,
): DocumentReach | null {
  if (document.chip === "discloses" || document.chip === "partial") {
    return document.chip;
  }
  if (document.status !== null) return null;
  const rung = document.findings.some((finding) =>
    finding.rungs.some((read) => read.disclosure !== "none"),
  );
  return rung ? "narrower" : null;
}

/** One rung a document discloses where it does not disclose the element above it. */
interface DisclosedFallback {
  key: string;
  text: string;
  reference: string;
}

/**
 * Every rung a document discloses in full beneath an element it does not
 * disclose in full, in the documents' order.
 *
 * The element keeps its reading, so the verdict and the headline stay as the
 * server wrote them; a rung the art already holds is no safe retreat, and the
 * attorney has to see that beside a "Clear" that would otherwise hide it. A
 * rung reached only in part is not named, and neither is one beneath an
 * element the document already discloses, where the chip says it all, nor one
 * a document whose reading could not be verified holds.
 */
function disclosedFallbacks(
  documents: readonly DocumentFindingsResponse[],
): DisclosedFallback[] {
  return documents
    .filter((document) => document.status === null)
    .flatMap((document) =>
      document.findings
        .filter((finding) => finding.disclosure !== "full")
        .flatMap((finding) =>
          finding.rungs
            .filter((rung) => rung.disclosure === "full")
            .map((rung) => ({
              key: `${document.file_id}-${rung.fallback_id}`,
              text: rung.text,
              reference: referenceOf(document),
            })),
        ),
    );
}

/** One line per disclosed rung, in the shape "fallback X is disclosed in D1". */
function DisclosedFallbacks({
  documents,
}: {
  documents: readonly DocumentFindingsResponse[];
}) {
  const lines = disclosedFallbacks(documents);
  if (lines.length === 0) return null;
  return (
    <ul className="mt-1 grid gap-0.5 text-[12px] text-muted-foreground">
      {lines.map((line) => (
        <li key={line.key} data-testid="disclosed-fallback">
          Fallback <span className="text-foreground">"{line.text}"</span> is
          disclosed in{" "}
          <span className="font-mono text-foreground">{line.reference}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Where a citation sits, in the record's own labels: its section and its
 * paragraph. The record fills ``paragraph`` from the nearest paragraph or
 * claim anchor, so the label may read "[0034]" or "Claim 3".
 */
function placeOf(citation: Citation): string {
  return [citation.section, citation.paragraph]
    .filter((label): label is string => label !== null)
    .join(" · ");
}

/**
 * The passages one document was cited at for this position: the quote, the
 * document and page as the provenance line names them, which is the way to
 * open the document at the passage the citation places, and the section
 * where the record names one.
 */
function PassageList({
  document,
  onOpenPassage,
}: {
  document: DocumentFindingsResponse;
  onOpenPassage: (fileId: string, citation: Citation) => void;
}) {
  const citations = document.findings.flatMap((f) => f.citations);
  const laddered = document.findings.some((f) => f.rungs.length > 0);
  if (citations.length === 0 && !laddered) {
    return (
      <p className="mt-2 text-[11.5px] text-muted-foreground">
        No passage was recorded for this document.
      </p>
    );
  }
  return (
    <ul data-testid="passage-list" className="mt-2 grid gap-1.5">
      {document.findings.map((finding) => (
        <Fragment key={finding.element_id}>
          {finding.citations.map((citation, at) => (
            <Panel
              as="li"
              shape="row"
              pad="tight"
              key={`${citation.page}-${citation.quote}-${String(at)}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-muted/40 text-[12px]"
            >
              <q className="flex-1 basis-full italic text-foreground">
                {citation.quote}
              </q>
              {/* The document and page in the provenance line's own form, as the
                  way in: one reading of a citation on every screen. */}
              <span className="font-mono text-[10.5px] text-muted-foreground">
                <OpenDocument
                  onOpen={() => onOpenPassage(document.file_id, citation)}
                >
                  {referenceOf(document)}
                  {document.cites_pages && ` (${pageReference(citation.page)})`}
                </OpenDocument>
                {placeOf(citation) !== "" && ` · ${placeOf(citation)}`}
              </span>
            </Panel>
          ))}
          <ElementLadder finding={finding} />
        </Fragment>
      ))}
    </ul>
  );
}

/** How far a document reaches, in the words the chips already use. */
const REACH = {
  full: "Discloses",
  partial: "Partly discloses",
  none: "Not disclosed",
} as const;

/** One rung as the ladder reads it: its kind, its words, and how far this document reaches it. */
function RungRow({ rung }: { rung: RungFindingResponse }) {
  return (
    <Panel
      as="li"
      shape="row"
      pad="tight"
      data-testid={`rung-${rung.fallback_id}`}
      className="flex flex-wrap items-baseline gap-x-2 gap-y-1 bg-muted/30 text-[12px]"
    >
      <MicroLabel size="sm">{rung.kind}</MicroLabel>
      <span className="flex-1 text-foreground">{rung.text}</span>
      <span
        className={cn(
          "font-mono text-[10.5px]",
          rung.disclosure === "none"
            ? "text-muted-foreground"
            : "text-foreground",
        )}
      >
        {REACH[rung.disclosure]}
      </span>
      {rung.what !== "" && (
        <span className="basis-full text-[11.5px] text-muted-foreground">
          {rung.what}
        </span>
      )}
    </Panel>
  );
}

/**
 * The narrower readings beneath one element, folded: the element's words on the
 * fold, a disclosure per rung inside it.
 *
 * Folded because the element's own reading is what the position turns on; the
 * ladder says how far this document got down it, which is read after.
 */
function ElementLadder({ finding }: { finding: ElementFindingResponse }) {
  const rungs = finding.rungs;
  const { open, trigger, panel } = useDisclosure();
  if (rungs.length === 0) return null;
  const reached = rungs.filter((rung) => rung.disclosure !== "none").length;
  return (
    <li data-testid={`ladder-${finding.element_id}`} className="mt-1.5">
      <DisclosureHeader
        trigger={trigger}
        open={open}
        title={<span className="text-foreground">{finding.text}</span>}
        meta={
          <span className="font-mono text-[10.5px]">
            {reached} of {plural(rungs.length, "narrower reading")} reached
          </span>
        }
        className="text-[11.5px] text-muted-foreground"
      />
      <ul {...panel} className="mt-1.5 grid gap-1.5 pl-5">
        {open &&
          rungs.map((rung) => <RungRow key={rung.fallback_id} rung={rung} />)}
      </ul>
    </li>
  );
}

/** What each reading is called on the chip, and the tone it is drawn in. */
const CHIP = {
  discloses: { label: "discloses", tone: "destructive" },
  partial: { label: "partial", tone: "warning" },
  narrower: { label: "narrower reading", tone: "neutral" },
} as const satisfies Record<DocumentReach, { label: string; tone: Tone }>;

/** One document that reached the position, as a chip: its reference and how far it reaches. */
function DocumentChip({
  document,
  reach,
  open,
  onToggle,
}: {
  document: DocumentFindingsResponse;
  reach: DocumentReach;
  open: boolean;
  onToggle: () => void;
}) {
  const reference = referenceOf(document);
  return (
    <button
      type="button"
      data-testid="document-chip"
      data-chip={reach}
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px] font-semibold",
        toneClasses(CHIP[reach].tone, "surface"),
        open && "ring-2 ring-primary/30",
      )}
    >
      <span className="font-mono">{reference}</span>
      <MicroLabel size="sm" className="text-current">
        {CHIP[reach].label}
      </MicroLabel>
    </button>
  );
}

/**
 * The "Against prior art" section: the headline, one chip per document that
 * reached the position, the passages behind the chip that is open, and the
 * sentence that says every position is re-checked automatically.
 */
export function AgainstPriorArt({
  headline,
  documents,
  onOpenPassage,
}: {
  headline: string;
  documents: readonly DocumentFindingsResponse[];
  onOpenPassage: (fileId: string, citation: Citation) => void;
}) {
  const [openFileId, setOpenFileId] = useState<string | null>(null);
  const reaching = documents.flatMap((document) => {
    const reach = reachOf(document);
    return reach === null ? [] : [{ document, reach }];
  });
  const open = reaching.find(
    (reached) => reached.document.file_id === openFileId,
  );
  return (
    <div data-testid="against-prior-art">
      <MicroLabel as="p" size="sm" className="mb-1">
        Against prior art
      </MicroLabel>
      <p className="text-[12.5px] font-semibold">{headline}</p>
      <DisclosedFallbacks documents={documents} />
      {reaching.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {reaching.map(({ document, reach }) => (
            <DocumentChip
              key={document.file_id}
              document={document}
              reach={reach}
              open={document.file_id === openFileId}
              onToggle={() =>
                setOpenFileId((current) =>
                  current === document.file_id ? null : document.file_id,
                )
              }
            />
          ))}
        </div>
      )}
      {open !== undefined && (
        <PassageList document={open.document} onOpenPassage={onOpenPassage} />
      )}
      <p className="mt-2 text-[11.5px] text-muted-foreground">
        Every position is re-checked against all loaded prior art automatically.
      </p>
    </div>
  );
}
